import React from 'react';
import { Phone, MapPin, Wrench, Home as HomeIcon, Building2, Clock, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const handleCallNow = () => {
    window.location.href = 'tel:920-336-9455';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="flex items-center justify-between md:justify-center md:relative">
            {/* Mobile: Logo on left, Desktop: Centered */}
            <div className="flex items-center md:flex-1 md:justify-center">
              <img 
                src="/images/turriff-logo-wide-color.png" 
                alt="Turriff Plumbing - Since 1920" 
                className="h-12 md:h-24 lg:h-28 w-auto object-contain"
              />
            </div>
            {/* Phone button - adapts for mobile and desktop */}
            <Button 
              onClick={handleCallNow}
              className="bg-primary-blue hover:bg-primary-blue-dark text-white px-4 py-2 md:px-6 md:py-3 text-sm md:text-lg font-semibold md:absolute md:right-4"
            >
              <Phone className="mr-1 md:mr-2 h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">920-336-9455</span>
              <span className="sm:hidden">Call</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/building-sketch.png)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Over 100 Years of
              <br />
              <span className="text-accent-blue">Trusted Service</span>
            </h2>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Professional plumbing solutions for residential and commercial clients throughout Northeast Wisconsin
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={handleCallNow}
                size="lg"
                className="bg-primary-blue hover:bg-primary-blue-dark text-white px-8 py-6 text-xl font-bold transform hover:scale-105 transition-transform"
              >
                <Phone className="mr-2 h-6 w-6" />
                Call Now: 920-336-9455
              </Button>
              <div className="flex items-center gap-2 text-white">
                <MapPin className="h-5 w-5" />
                <span>1110 Honey Ct, De Pere, WI</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h3>
            <p className="text-xl text-gray-600">Comprehensive Plumbing Solutions Since 1920</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Residential Services */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/8089159/pexels-photo-8089159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Residential Plumbing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <HomeIcon className="h-12 w-12 text-white mb-2" />
                </div>
              </div>
              <CardContent className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Residential Plumbing</h4>
                <p className="text-gray-600 mb-4">
                  Complete plumbing solutions for your home including repairs, maintenance, 
                  installations, and emergency services.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Wrench className="h-5 w-5 text-primary-blue mt-0.5 flex-shrink-0" />
                    <span>Repairs & Maintenance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wrench className="h-5 w-5 text-primary-blue mt-0.5 flex-shrink-0" />
                    <span>Fixture Installation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wrench className="h-5 w-5 text-primary-blue mt-0.5 flex-shrink-0" />
                    <span>Water Heaters</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Commercial Services */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/images/commercial-construction.jpg"
                  alt="Commercial Plumbing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Building2 className="h-12 w-12 text-white mb-2" />
                </div>
              </div>
              <CardContent className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Commercial & Construction</h4>
                <p className="text-gray-600 mb-4">
                  Expert commercial plumbing services for construction projects, renovations, 
                  and ongoing maintenance.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Wrench className="h-5 w-5 text-primary-blue mt-0.5 flex-shrink-0" />
                    <span>New Construction Plumbing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wrench className="h-5 w-5 text-primary-blue mt-0.5 flex-shrink-0" />
                    <span>Commercial Repairs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wrench className="h-5 w-5 text-primary-blue mt-0.5 flex-shrink-0" />
                    <span>System Maintenance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* We're Hiring Section */}
      <section className="py-24 bg-gradient-to-br from-primary-blue via-blue-600 to-blue-700 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center text-white">
            <div className="inline-block bg-white text-primary-blue px-8 py-3 rounded-full font-black text-lg mb-8 shadow-lg">
              🔧 NOW HIRING! 🔧
            </div>
            <h3 className="text-5xl md:text-7xl font-black mb-6 drop-shadow-lg">
              Join Our Team!
            </h3>
            <p className="text-2xl md:text-3xl mb-10 font-semibold text-white leading-relaxed">
              We're looking for talented plumbing professionals to join our 100+ year legacy.<br />
              <span className="text-accent-blue font-bold">Competitive pay • Great benefits • Career growth</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                onClick={() => window.location.href = '/careers'}
                size="lg"
                className="bg-white text-primary-blue hover:bg-gray-100 px-10 py-8 text-2xl font-black shadow-2xl transform hover:scale-105 transition-all"
              >
                <Briefcase className="mr-3 h-8 w-8" />
                View Open Positions
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Turriff Plumbing?</h3>
            <p className="text-xl text-gray-600 mb-12">
              Over a century of expertise and customer satisfaction
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <span className="text-3xl font-bold text-primary-blue">100+</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Years of Service</h4>
                <p className="text-gray-600">Serving the community since 1920</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Wrench className="h-8 w-8 text-primary-blue" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Expert Technicians</h4>
                <p className="text-gray-600">Licensed and experienced professionals</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Clock className="h-8 w-8 text-primary-blue" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Reliable Service</h4>
                <p className="text-gray-600">Prompt response and quality workmanship</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-primary-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-4xl font-bold mb-4">Need Plumbing Services?</h3>
          <p className="text-xl mb-8 text-blue-100">
            Call us today for reliable, professional plumbing solutions
          </p>
          
          <div className="flex flex-col items-center gap-6 max-w-2xl mx-auto">
            <Button 
              onClick={handleCallNow}
              size="lg"
              variant="secondary"
              className="bg-white text-primary-blue hover:bg-gray-100 px-10 py-6 text-2xl font-bold transform hover:scale-105 transition-transform"
            >
              <Phone className="mr-3 h-7 w-7" />
              920-336-9455
            </Button>
            
            <div className="flex flex-col sm:flex-row gap-6 text-lg">
              <div className="flex items-center gap-2">
                <MapPin className="h-6 w-6" />
                <span>1110 Honey Ct, De Pere, WI</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/images/turriff-logo.jpg" 
                  alt="Turriff Plumbing" 
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h4 className="text-xl font-bold">Turriff Plumbing</h4>
                  <p className="text-sm text-gray-400">Since 1920</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Over 100 years of trusted plumbing services in De Pere and surrounding areas.
              </p>
            </div>
            
            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Residential Plumbing</li>
                <li>Commercial Plumbing</li>
                <li>Construction Services</li>
                <li>Repairs & Maintenance</li>
              </ul>
            </div>
            
            {/* Careers */}
            <div>
              <h4 className="text-lg font-bold mb-4">We're Hiring!</h4>
              <p className="text-gray-300 text-sm mb-4">
                Join our team of skilled professionals and be part of our 100+ year legacy.
              </p>
              <a 
                href="/careers" 
                className="inline-block bg-accent-blue hover:bg-blue-400 text-white font-bold px-5 py-2.5 rounded-lg transition-colors text-sm"
              >
                View Openings →
              </a>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold mb-4">Contact</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>920-336-9455</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>1110 Honey Ct<br />De Pere, WI</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Turriff Plumbing. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
